/**
 * ConsumptionStep - Step 2: Energy consumption data.
 *
 * Four methods: Roczne (annual), Miesięczne (monthly), Import CSV, Zaimportowane.
 * ANNUAL: Slider + NumberInput, default 3000 kWh.
 * MONTHLY: 12 NumberInputs in grid, show sum.
 * CSV: File drop zone for Tauron eLicznik format.
 * IMPORTED: Uses data from existing "Import danych" module.
 */

import { useEffect, useRef } from 'react';
import { Stack, Text, SegmentedControl, NumberInput, SimpleGrid, Slider, Group, Badge, Button, Alert, Loader } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload, IconDatabase, IconCheck, IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';
import type { ProsumerFormValues, ConsumptionMethod } from '../../types';
import { useCSVImport } from '../../hooks/useCSVImport';
import { useImportedData } from '../../hooks/useImportedData';
import { useYearDataAvailability } from '../../../../hooks/useYearDataAvailability';

export interface ConsumptionStepProps {
  form: UseFormReturnType<ProsumerFormValues>;
}

const METHOD_OPTIONS = [
  { label: 'Roczne', value: 'ANNUAL' },
  { label: 'Miesięczne', value: 'MONTHLY' },
  { label: 'Import CSV', value: 'CSV' },
  { label: 'Zaimportowane', value: 'IMPORTED' },
];

const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

export function ConsumptionStep({ form }: ConsumptionStepProps) {
  const method = form.values.consumptionMethod;
  const csvImport = useCSVImport();
  const importedData = useImportedData(form.values.year);
  const yearAvailability = useYearDataAvailability();

  // Refetch import summary when user switches TO the IMPORTED tab (not on initial mount).
  // This catches the case where the user imported data in a different section/tab and
  // then switches to IMPORTED without navigating away from the wizard.
  const prevMethodRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      prevMethodRef.current !== null &&
      method === 'IMPORTED' &&
      prevMethodRef.current !== 'IMPORTED'
    ) {
      void importedData.refetch();
    }
    prevMethodRef.current = method;
  }, [method]); // importedData.refetch is stable when year is stable — intentionally omitted

  const shouldSuggestYear =
    !importedData.loading &&
    !importedData.hasImportedData &&
    yearAvailability.suggestedYear !== null &&
    yearAvailability.suggestedYear !== form.values.year;

  const handleMethodChange = (value: string) => {
    form.setFieldValue('consumptionMethod', value as ConsumptionMethod);
  };

  const handleCSVDrop = async (files: File[]) => {
    const parsed = await csvImport.handleFileAccept(files);
    if (parsed) {
      form.setFieldValue('csvData', parsed);
      form.setFieldValue('monthlyConsumptionKwh', parsed.monthlyConsumptionKwh);
      form.setFieldValue(
        'annualConsumptionKwh',
        Math.round(parsed.monthlyConsumptionKwh.reduce((a, b) => a + b, 0))
      );
    }
  };

  const handleLoadImportedData = async () => {
    const csvData = await importedData.loadData();
    if (csvData) {
      form.setFieldValue('csvData', csvData);
      // Also update monthly values for display
      form.setFieldValue('monthlyConsumptionKwh', csvData.monthlyConsumptionKwh);
      form.setFieldValue(
        'annualConsumptionKwh',
        Math.round(csvData.monthlyConsumptionKwh.reduce((a, b) => a + b, 0))
      );
    }
  };

  const monthlySum = form.values.monthlyConsumptionKwh.reduce((a, b) => a + b, 0);

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">Dane o zużyciu energii</Text>

      <SegmentedControl
        data={METHOD_OPTIONS}
        value={method}
        onChange={handleMethodChange}
        fullWidth
      />

      {method === 'ANNUAL' && (
        <Stack gap="sm">
          <Text size="sm" fw={500}>Roczne zużycie (kWh)</Text>
          <Slider
            min={500}
            max={50000}
            step={100}
            mb="xl"
            value={form.values.annualConsumptionKwh}
            onChange={(val) => form.setFieldValue('annualConsumptionKwh', val)}
            label={(val) => `${val} kWh`}
            marks={[
              { value: 500, label: '500' },
              { value: 10000, label: '10 000' },
              { value: 25000, label: '25 000' },
              { value: 50000, label: '50 000' },
            ]}
          />
          <NumberInput
            min={500}
            max={50000}
            step={100}
            suffix=" kWh"
            {...form.getInputProps('annualConsumptionKwh')}
          />
          <Text size="xs" c="dimmed">
            Typowe gospodarstwo domowe: 3 000–5 000 kWh/rok. Firmy i większe obiekty: do 50 000 kWh/rok.
          </Text>
        </Stack>
      )}

      {method === 'MONTHLY' && (
        <Stack gap="sm">
          <SimpleGrid cols={2}>
            {MONTH_NAMES.map((name, idx) => (
              <NumberInput
                key={name}
                label={name}
                min={0}
                max={5000}
                step={10}
                suffix=" kWh"
                value={form.values.monthlyConsumptionKwh[idx]}
                onChange={(val) => {
                  const newMonthly = [...form.values.monthlyConsumptionKwh];
                  newMonthly[idx] = typeof val === 'number' ? val : 0;
                  form.setFieldValue('monthlyConsumptionKwh', newMonthly);
                }}
              />
            ))}
          </SimpleGrid>
          <Group justify="flex-end">
            <Text size="sm">
              Suma: <Badge variant="light">{Math.round(monthlySum)} kWh</Badge>
            </Text>
          </Group>
        </Stack>
      )}

      {method === 'CSV' && (
        <Stack gap="sm">
          <Dropzone
            onDrop={handleCSVDrop}
            accept={{ 'text/csv': ['.csv'] }}
            maxSize={10 * 1024 * 1024}
          >
            <Group justify="center" p="lg">
              <IconUpload size={32} />
              <div>
                <Text size="sm" fw={500}>Przeciągnij plik CSV lub kliknij</Text>
                <Text size="xs" c="dimmed">Format Tauron eLicznik (.csv, max 10 MB)</Text>
              </div>
            </Group>
          </Dropzone>
          {csvImport.error && (
            <Text c="red" size="sm">{csvImport.error}</Text>
          )}
          {csvImport.data && (
            <Text c="green" size="sm">
              Załadowano dane: {csvImport.data.hourlyConsumptionData.length} rekordów
            </Text>
          )}
          {(() => {
            if (!form.values.csvData) return null;
            const nonZeroMonths = form.values.csvData.monthlyConsumptionKwh.filter((v) => v > 0).length;
            if (nonZeroMonths >= 12) return null;
            return (
              <Alert
                role="alert"
                icon={<IconAlertCircle size={16} />}
                color="yellow"
                title="Niepełne dane CSV"
              >
                Dane niepełne — wyniki będą ekstrapolowane z {nonZeroMonths} miesięcy
              </Alert>
            );
          })()}
        </Stack>
      )}

      {method === 'IMPORTED' && (
        <Stack gap="sm">
          {importedData.loading && (
            <Group justify="center" p="lg">
              <Loader size="sm" />
              <Text size="sm">Sprawdzanie dostępnych danych...</Text>
            </Group>
          )}

          {!importedData.loading && !importedData.hasImportedData && (
            <>
              {shouldSuggestYear ? (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  color="blue"
                  title="Dane dostępne dla innego roku"
                >
                  Brak danych dla roku {form.values.year}.
                  {yearAvailability.suggestedYearReason && ` ${yearAvailability.suggestedYearReason}.`}
                  <Button
                    variant="light"
                    size="xs"
                    mt="xs"
                    onClick={() => form.setFieldValue('year', yearAvailability.suggestedYear!)}
                  >
                    Przełącz na rok {yearAvailability.suggestedYear}
                  </Button>
                </Alert>
              ) : (
                <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="Brak zaimportowanych danych">
                  Nie znaleziono danych z modułu &quot;Import danych&quot; dla roku {form.values.year}.
                  Zaimportuj dane godzinowe w sekcji Konto → Import danych, lub wybierz inną metodę.
                </Alert>
              )}
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconAlertCircle size={14} />}
                  onClick={() => void importedData.refetch()}
                >
                  Odśwież
                </Button>
              </Group>
            </>
          )}

          {!importedData.loading && importedData.hasImportedData && (
            <>
              <Alert icon={<IconDatabase size={16} />} color="blue" title="Dostępne dane">
                Rok {form.values.year}: {importedData.availableMonths}/12 miesięcy,
                {' '}{Math.round(importedData.totalConsumption).toLocaleString('pl-PL')} kWh łącznie
                {importedData.isFullYear && (
                  <Badge ml="xs" size="xs" color="green" variant="light">Pełny rok</Badge>
                )}
              </Alert>

              {!importedData.data ? (
                <Button
                  leftSection={<IconDatabase size={16} />}
                  onClick={() => void handleLoadImportedData()}
                  loading={importedData.loading}
                >
                  Załaduj dane godzinowe
                </Button>
              ) : (
                <Alert icon={<IconCheck size={16} />} color="green" title="Dane załadowane">
                  Załadowano {importedData.data.hourlyConsumptionData.length} rekordów godzinowych
                </Alert>
              )}
            </>
          )}

          {importedData.error && !importedData.loading && (
            <Text c="red" size="sm">{importedData.error}</Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
